import { useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { PageShell } from '@/components/shared/layout'
import { PageHeader } from '@/components/shared/layout/PageHeader'
import { SettingsTabs, SettingsBody } from '@/components/shared/settings/SettingsKit'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { usePropertySafe } from '@/lib/contexts/PropertyContext'
import InstallTab from '@/components/site-settings/InstallTab'
import GeneralTab from '@/components/site-settings/GeneralTab'
import IPFilteringTab from '@/components/site-settings/IPFilteringTab'
import NotificationsTab from '@/components/site-settings/NotificationsTab'
import DangerZoneTab from '@/components/site-settings/DangerZoneTab'

interface DashboardContext {
  propertyId?: string
}

const tabs = [
  { id: 'install', label: 'Install' },
  { id: 'general', label: 'General' },
  { id: 'ip-filtering', label: 'IP filtering' },
  { id: 'notifications', label: 'Email reports' },
  { id: 'danger-zone', label: 'Danger zone' },
] as const

type TabId = (typeof tabs)[number]['id']

export default function SiteSettings() {
  const ctx = useOutletContext<DashboardContext>()
  const propertyCtx = usePropertySafe()
  const site = useSiteSafe()

  // Prefer PropertyContext (bound to URL slug) over SiteContext (defaults to first property)
  const propertyId = propertyCtx?.property?.propertyId || ctx?.propertyId || ''
  // The bridged Property keeps its domain on the website data source, not on
  // `property.domain`; reading that undefined field fell back to SiteContext's
  // default site, so every tab (Install included) showed another site's domain.
  const propertyDomain = (propertyCtx?.property?.dataSources?.[0]?.config as { domain?: string } | undefined)?.domain
  const domain = propertyDomain || (propertyId && site?.currentSite?.propertyId === propertyId ? site.currentSite.domain : '') || ''

  // Deep-link support: hub/list actions route here with ?tab=install|general|… so
  // there's a single canonical site-settings surface (the modal was retired).
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTab = tabs.find((t) => t.id === tabParam)?.id ?? 'general'
  const [activeTab, setActiveTab] = useState<TabId>(initialTab)

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId)
    // Keep the URL shareable without stacking history entries.
    const next = new URLSearchParams(searchParams)
    if (tabId === 'general') next.delete('tab')
    else next.set('tab', tabId)
    setSearchParams(next, { replace: true })
  }

  return (
    <PageShell maxWidth="max-w-none">
      <PageHeader
        title="Site settings"
        description={`Install script, filters and email reports for ${domain || 'this site'}.`}
        className="mb-0"
      />
      <SettingsTabs label="Site settings" tabs={tabs} active={activeTab} onChange={handleTabChange} />

      <SettingsBody>
        {activeTab === 'install' && <InstallTab domain={domain} propertyId={propertyId} />}
        {activeTab === 'general' && <GeneralTab domain={domain} propertyId={propertyId} />}
        {activeTab === 'ip-filtering' && <IPFilteringTab domain={domain} propertyId={propertyId} />}
        {activeTab === 'notifications' && <NotificationsTab domain={domain} propertyId={propertyId} />}
        {activeTab === 'danger-zone' && <DangerZoneTab domain={domain} propertyId={propertyId} />}
      </SettingsBody>
    </PageShell>
  )
}
